import React from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { ReservationFlagTypeEnum, ReservationListPreviewOutDTO } from '@api/ringil-api.v2';
import { isNotBlank, isNotNullOrUndefined, isNullOrUndefined, visualiseWorkflowStateState } from '@utils/utils';
import { formatDateFromStringWithLocale, stringHHmmFromStringInTz, stringYMDFromStringInTz } from '@utils/dateWithTz';
import SecuredArea from '@components/security/SecuredArea';
import EllipsisText from '@components/RingilComponents/EllipsisText';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import ExpandableLabelWrapper from '@components/RingilDataTable/ExpandableLabelWrapper.tsx';
import ExpandableInfoAllTag from '@components/RingilDataTable/ExpandableInfoAllTag.tsx';
import ExpandableInfoTag from '@components/RingilDataTable/ExpandableInfoTag.tsx';
import AttachmentLink from '@components/RingilDataTable/AttachmentLink.tsx';
import { FlagIcon, sortFlags } from '@components/ringil3/Features/Reservation/flagIcon';
import { flagUnitsText } from '@components/RingilComponents/form/flagUnitsLimit';
import type { FieldDefinition } from '@components/RingilDataTable/v3/columns/buildColumns';
import LocationTargetSvg from '@icons/LocationTargetSvg.tsx';
import CalendarSvg from '@icons/calendar.svg?react';
import ClockSvg from '@icons/ClockSvg.tsx';
import BuildingSvg from '@icons/BuildingSvg.tsx';
import TruckSvg from '@icons/TruckSvg.tsx';
import SvgChevron from '@icons/chevron.svg?react';
import SvgLink from '@icons/link.svg?react';
import SvgOrder from '@icons/order.svg?react';
import { ReservationField as F, ReservationField, ReservationFieldGroup as Group, reservationInfoTagsCollapsible } from './reservationFieldKeys';

/**
 * The reservation list's field catalogue for RingilDataTable v3 (RD-2957).
 *
 * Every field is a piece of one of today's five cells, drawn with the component that cell uses, so
 * taking a cell apart never downgrades a value. The presets put the pieces back together and the
 * default tab is made of them (in the company's column order). Test ids are the cells' own.
 */

/** The additional-information tags whose expanded state the list remembers: each flag type, orders, attachments. */
export type ReservationInfoTag = ReservationFlagTypeEnum | 'orders' | 'attachments';

type Row = ReservationListPreviewOutDTO & { driverInfo?: { driverName?: string } };

export interface ReservationFieldContext {
  tr: (key: string, fallback: string, params?: Record<string, unknown>) => string;
  detailLink: (row: Row) => string;
  /** The logged-in user's company owns the reservation (the customer side). */
  isOwner: (row: Row) => boolean;
  infoTags: { isExpanded: (tag: ReservationInfoTag | 'all') => boolean; toggle: (tag: ReservationInfoTag) => void; setAll: (expanded: boolean) => void };
  labels: { expanded: boolean; toggle: () => void };
  onAttachmentClick: (attachmentId: string, fileName: string) => void;
}

// Ringil 2.0 caption: 12/16, secondary — the second line of a cell.
const muted = 'text-xs leading-4 text-content-secondary';

const text = (value?: string | number | null, lines = 1) =>
  isNotNullOrUndefined(value) && value !== '' ? <EllipsisText lines={lines}>{String(value)}</EllipsisText> : null;

/** A slot to show: a migrated one needs its timezone, an old one its Prague times. */
const hasReservation = (row: Row): boolean => {
  if (isNullOrUndefined(row?.timeslot)) return false;
  if (isNullOrUndefined(row.timeslot.since) && isNotNullOrUndefined(row.timeslot.utcStart)) return false;
  if (isNullOrUndefined(row.timeslot.till) && isNotNullOrUndefined(row.timeslot.utcEnd)) return false;
  if (isNotBlank(row.timeslot.utcStart) && isNullOrUndefined(row.timeslot.timezoneId)) return false;
  return true;
};

const slotParts = (row: Row) => {
  const migrated = isNotNullOrUndefined(row.timeslot?.timezoneId) && isNotNullOrUndefined(row.timeslot?.utcStart) && isNotNullOrUndefined(row.timeslot?.utcEnd);
  const tz = migrated ? row.timeslot.timezoneId : 'Europe/Prague';
  return {
    date: stringYMDFromStringInTz(migrated ? row.timeslot.utcStart : row.timeslot?.since, tz),
    since: stringHHmmFromStringInTz(migrated ? row.timeslot.utcStart : row.timeslot?.since, tz),
    till: stringHHmmFromStringInTz(migrated ? row.timeslot.utcEnd : row.timeslot?.till, tz),
  };
};

/** Orders' cargo summed per cargo type — "3x Pallet, 2x Box". */
const manipulationUnits = (row: Row): string =>
  (row.orders ?? [])
    .flatMap(order => order.items ?? [])
    .reduce((acc, item) => {
      const existing = acc.find(t => t.cargoTemplate.code === item.cargoTemplate.code);
      if (existing) existing.amount += item.amount;
      else acc.push({ cargoTemplate: item.cargoTemplate, amount: item.amount });
      return acc;
    }, [] as { cargoTemplate: { code: string; name: string }; amount: number }[])
    .map(t => `${t.amount}x ${t.cargoTemplate.name}`)
    .join(', ');

/** Builds the catalogue. Called on every render of the list, so each field sees the current expanded state — never cache it. */
export const buildReservationFields = (ctx: ReservationFieldContext): FieldDefinition<Row>[] => {
  const fields = reservationFieldDefinitions(ctx);
  if (import.meta.env.DEV) {
    const missing = Object.values(ReservationField).filter(key => !fields.some(field => field.key === key));
    if (missing.length > 0) console.error('Reservation v3 catalogue: keys without a field definition', missing);
  }
  return fields;
};

const reservationFieldDefinitions = (ctx: ReservationFieldContext): FieldDefinition<Row>[] => {
  const { tr, infoTags } = ctx;
  const tag = (key: ReservationInfoTag) => ({ expanded: infoTags.isExpanded(key), onClick: () => infoTags.toggle(key) });

  return [
    // ------------------------------------------------------------------ reservation
    {
      key: F.ApplicationId,
      group: Group.Reservation,
      header: 'reservations.applicationId',
      headerDefault: 'Application ID',
      sortField: 'applicationId',
      minWidth: 120,
      identity: true,
      required: true,
      render: row => (
        // data-row-open: in the split view a plain click opens the pane; Ctrl/Cmd-click still opens a new tab.
        <Link to={ctx.detailLink(row)} className="block min-w-0 font-medium" data-row-open data-testid={`reservations-row-${row.applicationId}-applicationId`}>
          <EllipsisText>{`${row.applicationId}${row.hasETransportRO ? ' (E)' : ''}`}</EllipsisText>
        </Link>
      ),
    },
    {
      key: F.State,
      group: Group.Reservation,
      header: 'reservations.states',
      headerDefault: 'State',
      minWidth: 120,
      fitWidth: 110,
      render: row => (row.state ? <span>{visualiseWorkflowStateState(row.state, tr, false)}</span> : null),
    },
    {
      key: F.OnBehalfOf,
      group: Group.Reservation,
      header: 'reservations.onBehalfOf',
      headerDefault: 'On behalf of',
      minWidth: 140,
      render: row =>
        row.onBehalfOf ? (
          <div className={clsx(muted, 'font-medium')} data-testid={`reservations-row-${row.applicationId}-onBehalfOf`}>
            {text(row.onBehalfOf.name ?? row.onBehalfOf.externalId)}
          </div>
        ) : null,
    },
    {
      key: F.Location,
      group: Group.Reservation,
      header: 'reservations.locality',
      headerDefault: 'Locality',
      minWidth: 140,
      render: row =>
        isNotBlank(row.locationName) ? (
          <div className={muted} data-testid={`reservations-row-${row.applicationId}-locationName`}>
            {text(row.locationName)}
          </div>
        ) : null,
    },
    {
      key: F.Labels,
      group: Group.Reservation,
      header: 'generic.labels',
      headerDefault: 'Labels',
      minWidth: 160,
      render: row => (
        <SecuredArea
          id={`row-labels-${row.applicationId}`}
          visible={[
            { type: 'ownership', label: 'logged-in company owns reservation', satisfied: ctx.isOwner(row) },
            { type: 'data', label: 'labels.length > 0', satisfied: row.labels?.length > 0 },
          ]}
          label="Reservation labels"
        >
          <ExpandableLabelWrapper labels={row.labels} isExpanded={ctx.labels.expanded} onToggle={ctx.labels.toggle} />
        </SecuredArea>
      ),
    },

    // ------------------------------------------------------------------ ramp and time
    {
      key: F.Ramp,
      group: Group.RampAndTime,
      header: 'reservations.ramp',
      headerDefault: 'Ramp',
      minWidth: 120,
      render: row =>
        isNotBlank(row.rampName) ? (
          <div className="flex min-w-0 items-center gap-2 font-medium" data-testid={`reservations-row-${row.applicationId}-ramp`}>
            <LocationTargetSvg />
            <EllipsisText>{row.rampName}</EllipsisText>
          </div>
        ) : null,
    },
    {
      key: F.TimeslotDate,
      group: Group.RampAndTime,
      header: 'reservations.timeslotDate',
      headerDefault: 'Date',
      sortField: 'timeslot.since',
      minWidth: 110,
      fitWidth: 100,
      render: row =>
        hasReservation(row) ? (
          <div className="flex items-center gap-2 whitespace-nowrap" data-testid={`reservations-row-${row.applicationId}-timeslotDate`}>
            <CalendarSvg className="shrink-0 text-content-secondary" />
            {slotParts(row).date}
          </div>
        ) : null,
    },
    {
      key: F.TimeslotTime,
      group: Group.RampAndTime,
      header: 'reservations.timeslotTime',
      headerDefault: 'Time',
      minWidth: 110,
      fitWidth: 100,
      render: row => {
        if (!hasReservation(row)) return null;
        const { since, till } = slotParts(row);
        return (
          <div className={clsx(muted, 'flex items-center gap-2 whitespace-nowrap')} data-testid={`reservations-row-${row.applicationId}-timeslotTimeRange`}>
            <ClockSvg />
            {since} - {till}
          </div>
        );
      },
    },
    {
      key: F.TimeslotTimezone,
      group: Group.RampAndTime,
      header: 'generic.timezone',
      headerDefault: 'Timezone',
      minWidth: 110,
      render: row =>
        hasReservation(row) && row.timeslot?.timezoneId ? (
          <COTimezone dataTestId={`reservations-row-${row.applicationId}-timezone`} timezoneId={row.timeslot.timezoneId} />
        ) : null,
    },

    // ------------------------------------------------------------------ customer and carrier
    {
      key: F.Customer,
      group: Group.CustomerAndCarrier,
      header: 'reservations.customerName',
      headerDefault: 'Customer',
      minWidth: 140,
      render: row =>
        isNotBlank(row.customerName) ? (
          <div className="flex min-w-0 items-center gap-2 font-medium" data-testid={`reservations-row-${row.applicationId}-customerName`}>
            <BuildingSvg />
            <EllipsisText>{row.customerName}</EllipsisText>
          </div>
        ) : null,
    },
    {
      key: F.Carrier,
      group: Group.CustomerAndCarrier,
      header: 'reservations.carrierName',
      headerDefault: 'Carrier',
      minWidth: 140,
      render: row =>
        isNotBlank(row.carrierName) ? (
          <div className={clsx(muted, 'flex min-w-0 items-center gap-2')} data-testid={`reservations-row-${row.applicationId}-carrierName`}>
            <TruckSvg />
            <EllipsisText>{row.carrierName}</EllipsisText>
          </div>
        ) : null,
    },
    {
      key: F.Driver,
      group: Group.CustomerAndCarrier,
      header: 'reservations.driverAndCarPlate',
      headerDefault: 'Driver and car plate',
      minWidth: 140,
      render: row => {
        const value = [row.driverInfo?.driverName, row.carPlate].filter(t => t != null && t !== '').join(', ');
        return value ? (
          <div className={muted} data-testid={`reservations-row-${row.applicationId}-carPlate`}>
            {text(value)}
          </div>
        ) : null;
      },
    },

    // ------------------------------------------------------------------ reference
    {
      key: F.ReservationName,
      group: Group.Reference,
      header: 'reservations.reservationName',
      headerDefault: 'Reservation name',
      minWidth: 140,
      render: row =>
        isNotBlank(row.reservationName) ? (
          <div className="font-medium" data-testid={`reservations-row-${row.applicationId}-reservationName`}>
            {text(row.reservationName, 2)}
          </div>
        ) : null,
    },
    {
      key: F.OrderNames,
      group: Group.Reference,
      header: 'reservations.referenceID',
      headerDefault: 'Reference',
      minWidth: 140,
      render: row => {
        const names = row.orders?.map(order => order.orderName).join(', ');
        return names ? (
          <div className={muted} data-testid={`reservations-row-${row.applicationId}-orderName`}>
            {text(names, 2)}
          </div>
        ) : null;
      },
    },
    {
      key: F.PreferredDate,
      group: Group.Reference,
      header: 'reservations.preferredDate',
      headerDefault: 'Preferred date',
      minWidth: 110,
      // A calendar date: shown as the day it is, never shifted through a timezone (west of UTC that showed the day before).
      render: row => (row.preferredDate ? text(formatDateFromStringWithLocale(row.preferredDate.slice(0, 10))) : null),
    },

    // ------------------------------------------------------------------ additional information (tags)
    {
      key: F.InfoExpandAll,
      group: Group.AdditionalInfo,
      header: 'generic.expandAll',
      headerDefault: 'Expand all',
      inline: true,
      exportable: false,
      render: row => {
        const any = row.flags?.length > 0 || isNotBlank(manipulationUnits(row)) || row.attachmentInfo?.attachmentSize > 0;
        if (!any) return null;
        const all = infoTags.isExpanded('all');
        return (
          <ExpandableInfoAllTag
            id={`flags_all_${row.id}`}
            dataTestId={`reservations-row-${row.applicationId}-flags-all`}
            onClick={() => infoTags.setAll(!all)}
            tooltipText={all ? tr('expandableInfoTag.collapseAll', 'Collapse all') : tr('expandableInfoTag.expandAll', 'Expand all')}
            icon={<SvgChevron className={clsx('text-content-brand', all && 'rotate-180')} />}
          />
        );
      },
    },
    {
      key: F.Flags,
      group: Group.AdditionalInfo,
      header: 'reservations.flags',
      headerDefault: 'Flags',
      inline: true,
      render: (row, _toggleExpandedRow, cell) => {
        if (!(row.flags?.length > 0)) return null;
        const flags = sortFlags(row.flags);
        const label = (f: (typeof flags)[number]) => {
          const units = flagUnitsText(f);
          return `${tr(`ReservationFlag.${f.reservationFlagType}`, f.reservationFlagType)}${
            units != null ? tr('CalendarEventCardDetail.flagUnits', ' ({{units}})', { units }) : ''
          }`;
        };
        // Out of the additional-information column: the values alone, as any other column shows them.
        if (!reservationInfoTagsCollapsible(cell.members)) return text(flags.map(label).join(', '), 3);
        return (
          <>
            {flags.map(f => (
              <div key={f.reservationFlagType} data-testid={`reservations-row-${row.applicationId}-flag-${f.reservationFlagType}`}>
                <ExpandableInfoTag
                  id={`flag_${f.reservationFlagType}_${row.id}`}
                  {...tag(f.reservationFlagType)}
                  label={tr(`ReservationFlag.${f.reservationFlagType}`, f.reservationFlagType)}
                  text={flagUnitsText(f) != null ? tr('CalendarEventCardDetail.flagUnits', ' ({{units}})', { units: flagUnitsText(f) }) : undefined}
                  icon={<FlagIcon type={f.reservationFlagType} className="w-3 h-3 m-1" />}
                />
              </div>
            ))}
          </>
        );
      },
    },
    {
      key: F.Orders,
      group: Group.AdditionalInfo,
      header: 'reservations.manipulationUnits',
      headerDefault: 'Handling units',
      inline: true,
      render: (row, _toggleExpandedRow, cell) => {
        const units = manipulationUnits(row);
        if (!isNotBlank(units)) return null;
        if (!reservationInfoTagsCollapsible(cell.members)) return text(units, 3);
        return (
          <ExpandableInfoTag
            id={`orders_${row.id}`}
            dataTestId={`reservations-row-${row.applicationId}-orders`}
            {...tag('orders')}
            label={tr('reservations.ordersTagLabel', 'Orders: ')}
            text={units}
            icon={<SvgOrder />}
            count={row.orders?.length || 0}
          />
        );
      },
    },
    {
      key: F.Attachments,
      group: Group.AdditionalInfo,
      header: 'generic.attachments',
      headerDefault: 'Attachments',
      inline: true,
      render: (row, _toggleExpandedRow, cell) => {
        if (!(row.attachmentInfo?.attachmentSize > 0)) return null;
        const links = (
          <span className="font-medium">
            {row.attachmentInfo.attachments?.map((attachment, idx) => (
              <React.Fragment key={attachment.id}>
                <AttachmentLink id={attachment.id} name={attachment.name} onActivate={ctx.onAttachmentClick} maxLength={15} />
                {idx < row.attachmentInfo.attachments.length - 1 && <span className="text-content-tertiary">{' | '}</span>}
              </React.Fragment>
            ))}
          </span>
        );
        // The one value that does something: each file still downloads on click.
        if (!reservationInfoTagsCollapsible(cell.members)) return <div className="text-sm">{links}</div>;
        return (
          <ExpandableInfoTag
            id={`attachments_${row.id}`}
            dataTestId={`reservations-row-${row.applicationId}-attachments`}
            {...tag('attachments')}
            icon={<SvgLink className={infoTags.isExpanded('attachments') ? 'text-content-brand' : ''} />}
            label={tr('reservations.attachments', 'Attachments: ')}
            count={row.attachmentInfo.attachmentSize}
            text={row.attachmentInfo.attachments?.map(a => a.name).join(' | ')}
            textComponent={links}
          />
        );
      },
    },
  ];
};
